import { useEffect } from "react";
import { Check } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CTASection from "@/components/CTASection";

export default function Services() {
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-1 pt-20">
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-r from-seafarer-orange/10 to-seafarer-purple/10">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-white text-4xl md:text-5xl font-bold mb-6">Our Services</h1>
              <p className="text-lg text-white">
                We offer a comprehensive range of services to support our mission of providing
                quality housing and improving the welfare of fishermen and their families.
              </p>
            </div>
          </div>
        </section>
        
        {/* Services Overview */}
        <section className="section-padding">
          <div className="page-container">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-white text-3xl font-bold mb-6">Comprehensive Housing Solutions</h2>
              <p className="text-white mb-6">
                At October Seafarers United Land & Housing Federation, we combine skill, knowledge, 
                and goodwill, with a high sense of unity and trust. We commit synergy and mediate 
                with professionals and technical experts for advice and implementation of projects 
                that will enable us to deliver serviced land and build affordable housing at reasonable prices.
              </p>
              
              <h3 className="text-white text-xl font-semibold mb-4">Our Approach:</h3>
              <ul className="text-white space-y-3 mb-6">
                {[
                  "Professional project management and execution",
                  "Quality materials and construction techniques",
                  "Affordable and transparent pricing",
                  "Sustainable and environmentally conscious development",
                  "Community-centered planning and design"
                ].map((item, index) => (
                  <li key={index} className="flex items-start">
                    <Check className="h-5 w-5 text-seafarer-orange mr-2 mt-0.5" />
                    <span className="text-white">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
        
        {/* Service Categories */}
        <section className="section-padding bg-gray-50 dark:bg-gray-800">
          <div className="page-container">
            <div className="max-w-3xl mx-auto text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Our Products & Services</h2>
              <p className="text-lg text-gray-600 dark:text-gray-300">
                We provide a wide range of services to meet the housing needs of fishermen and their families.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  title: "Property Development",
                  items: [
                    "Land acquisition and preparation",
                    "Housing development projects",
                    "Infrastructure development",
                    "Sustainable community planning"
                  ]
                },
                {
                  title: "Project Management",
                  items: [
                    "Construction project oversight",
                    "Timeline and budget management",
                    "Quality control and assurance",
                    "Stakeholder coordination"
                  ]
                },
                {
                  title: "Building & Design",
                  items: [
                    "Architectural design",
                    "Interior design & space planning",
                    "Energy-efficient building solutions",
                    "Custom home designs"
                  ]
                },
                {
                  title: "Property Management",
                  items: [
                    "Property maintenance",
                    "Tenant relations",
                    "Financial management",
                    "Community governance"
                  ]
                },
                {
                  title: "Land Development",
                  items: [
                    "Land and development planning",
                    "Groundwork & foundation",
                    "Fencing, gates & barriers",
                    "Environmental consideration"
                  ]
                },
                {
                  title: "Safety & Management",
                  items: [
                    "Health, safety & risk management",
                    "Building management & control",
                    "Construction supervision",
                    "Regulatory compliance"
                  ]
                }
              ].map((category, index) => (
                <div 
                  key={category.title}
                  className="seafarer-card p-6 hover:shadow-lg transition-all duration-300 animate-fade-in"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <h3 className="text-xl font-bold mb-4">{category.title}</h3>
                  <ul className="space-y-2">
                    {category.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start">
                        <Check className="h-5 w-5 text-seafarer-orange mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-600 dark:text-gray-300">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>
        
        <CTASection />
      </main>
      
      <Footer />
    </div>
  );
}
